import { Observable, Subscription } from "rxjs";
import { NamedBlob, NormalizeByValue, SerieUid } from "../dicom-common";
import { DicomSerie, dicomSUVNormalization } from "../dicom-definitions";
import { DicomAnalysisResultSerie } from "../dicom.service";
import { CdssModel } from "./model";
import { FileUploadService } from "./file-upload.service";

export function prepareSeriesForUpload(roleAssignment: Map<string,string>, loadedFiles: Map<string, NamedBlob> | null,
    serieUidMap: Map<SerieUid, DicomAnalysisResultSerie>, model: CdssModel,
    dicomValues: object): (DicomSerie & NormalizeByValue & { roleId: string })[] {

    
    let series = new Map<string, DicomSerie & NormalizeByValue & { roleId: string }>();
    let roleAssignmentForServer = new Map<string, string>(); // role.id -> uploaded filename

    // Prepare for Nifti upload, unless the app is DICOM-only
    if (model.imagingFormat !== 'dicom') {
      model.data.roles.forEach(role => {
        let serieUid = roleAssignment.get(role.id);
    
        const normalizeLinear = dicomSUVNormalization(role, dicomValues);
        let filename: string;
    
        if (normalizeLinear === undefined)
          filename = `${serieUid}.nii.gz`;
        else
          filename = `${serieUid}-SUV.nii.gz`;
    
        if (!series.has(filename)) {
          const serieData = serieUidMap.get(serieUid);
          let files = loadedFiles ? serieData.filenames.map(filename => loadedFiles.get(filename)) : null;
    
          series.set(filename, {
            files,
            filenames: serieData.filenames,
            targetFileName: filename,
            friendlyName: serieData.description || serieData.modality,
            normalizeByValue: normalizeLinear,
            roleId: role.id,
          });
        }

        roleAssignmentForServer.set(role.id, filename);
      });
    }

    // Prepare for DICOM upload, unless the app is Nifti-only. Assume nifti if not set.
    if (model.imagingFormat && model.imagingFormat !== 'nifti') {
      model.data.roles.forEach(role => {
        const serieUid = roleAssignment.get(role.id);

        if (!series.has(serieUid)) {
          const filename = serieUid; // Store under directory named after series UID
          const serieData = serieUidMap.get(serieUid);
          let files = loadedFiles ? serieData.filenames.map(filename => loadedFiles.get(filename)) : null;
    
          series.set(filename, {
            files,
            filenames: serieData.filenames,
            targetFileNameDicom: filename,
            friendlyName: serieData.description || serieData.modality,
            normalizeByValue: undefined,
            roleId: role.id,
          });
        }
      });
    }

    //dicomUpload.submitDicomSeries(Array.from(series.values()));
    return Array.from(series.values());
  }

  // Converts a role id -> series UID map into role id -> filename
export function roleAssignmentForServer(model: CdssModel, roleAssignment: Map<string,string>): Map<string,string> {
  const roleAssignmentForServer = new Map<string, string>(); // role.id -> uploaded filename

  if (model.imagingFormat === 'dicom')
    return roleAssignmentForServer; // No Nifti upload

  model.data.roles.forEach(role => {
    let serieUid = roleAssignment.get(role.id);
    let filename: string;

    if (!role.normalizeToSUV)
      filename = `${serieUid}.nii.gz`;
    else
      filename = `${serieUid}-SUV.nii.gz`;


    roleAssignmentForServer.set(role.id, filename);
  });

  return roleAssignmentForServer;
}

export function roleAssignmentForServerDicom(model: CdssModel, roleAssignment: Map<string,string>): Map<string,string> {
  const roleAssignmentForServer = new Map<string, string>(); // role.id -> uploaded directory

  if (model.imagingFormat === 'nifti' || !model.imagingFormat)
    return roleAssignmentForServer; // No DICOM upload

  model.data.roles.forEach(role => {
    const serieUid = roleAssignment.get(role.id);
    const directory = serieUid; // Store under directory named after series UID

    roleAssignmentForServer.set(role.id, directory);
  });

  return roleAssignmentForServer;
}

const bulkSize = 10;

export function anonymizeAndUploadDicom<FileType>(batchId: string, files: FileType[],
  anonymizer: (input: FileType[]) => Promise<NamedBlob[]>,
  fileUploadService: FileUploadService, filePrefix: string,
): Observable<number> {
  return new Observable<number>(subscriber => {
    let aborted = false;

    const implementation = async () => {
      let ongoingUploads: Subscription[] = [];

      let nextAnonymizedBulk: Promise<NamedBlob[]>;
      let filesDone = 0;

      // Interlace anonymizing and uploading:
      // First iteration will only anonymize
      // Last iteration will only upload
      // Iterations in between will do both
      for (let i = 0; i < files.length + bulkSize; i += bulkSize) {
        let uploadDonePromise: Promise<any[]>;

        if (aborted) {

          // Terminate all ongoing uploads
          ongoingUploads.forEach(s => s.unsubscribe());

          return;
        }

        if (nextAnonymizedBulk) {
          uploadDonePromise = nextAnonymizedBulk.then(anonymizedBulk => {
            const all = [];

            for (const file of anonymizedBulk) {
              const promise = new Promise<void>((resolve, reject) => {
                const sub = fileUploadService.uploadFile(batchId, filePrefix + file.name, file.data)
                  .subscribe({
                    complete: () => {
                      console.debug('Upload complete:', file.name);
                      ongoingUploads = ongoingUploads.filter(s => s !== sub);
                      resolve();
                    },
                    error: err => {
                      console.error('Upload error:', file.name, err);
                      ongoingUploads = ongoingUploads.filter(s => s !== sub);
                      reject(err);
                    },
                  });
                ongoingUploads.push(sub);
              });

              all.push(
                promise.then(() => {
                  filesDone++;
                  subscriber.next(Math.round(filesDone / files.length * 100));
                })
              );
            }

            return Promise.all(all);
          });
        }

        if (i < files.length) {
          const bulk = files.slice(i, i + bulkSize);
          nextAnonymizedBulk = anonymizer(bulk);
        }

        if (uploadDonePromise) {
          await uploadDonePromise;
        }
      }
    };

    implementation().then(() => {
      subscriber.complete();
    }).catch(err => {
      subscriber.error(err);
    });

    return () => {
      aborted = true;
    };
  });
}
